// Pure measurement-overlay renderer — no viewer/store access, only a 2D canvas context
// and two pre-projected screen points. Draws the dimension line, endpoint circles, the
// distance box, the close (✕) button, and the X/Y/Z component panels, and returns the
// close-icon hit info so each caller wires its own hit-test.
//
// Shared by the 3D MeasurementExtension (drawDimensionLine) and the 360 panorama overlay
// (pageView.updatePanoDimensionLine) so both surfaces render pixel-identically. The two
// were previously near-duplicate copies that drifted apart; this is the single source.
//
// The caller owns clearRect and the screen-space projection; this module only draws.
import { midpoint } from '@/utils/threejsUtils';
import { roundRect } from '@/scripts/js/forgeService';

export const MEASUREMENT_COLORS = {
  MAIN_LINE: '#1dc7f3',
  AXIS_X: '#e62c2c',
  AXIS_Y: '#0BB80B',
  AXIS_Z: '#2C2CF1',
  CLOSE_BTN: '#ff4444',
  TEXT: '#ffffff',
  INTERSECTION: '#ff0000',
  INTERSECTION_BORDER: '#ffff00',
  TRANSPARENT: 'rgba(1, 1, 1, 0)',
};

// Padding added to the drawn close-icon radius when hit-testing clicks, so the tap target
// is larger than the glyph. Exported so the 3D (MeasurementExtension) and 360 (forgeService)
// hit-tests share one value — previously 3D used radius+8 and 360 used the bare radius (B4).
export const CLOSE_ICON_HIT_PADDING = 8;

function drawIntersectionCircle(ctx, point, colors) {
  const radius = 4;
  const borderWidth = 2;
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.arc(point.x, point.y, radius, 0, 2 * Math.PI, false);
  ctx.fillStyle = colors.INTERSECTION;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(point.x, point.y, radius + borderWidth, 0, 2 * Math.PI, false);
  ctx.strokeStyle = colors.INTERSECTION_BORDER;
  ctx.lineWidth = borderWidth;
  ctx.stroke();
}

// Draws the ✕ button just outside the top-right corner of the distance box.
// Returns { centerX, centerY, radius } for the caller's click hit-test.
function drawCloseIcon(ctx, rectX, rectY, rectWidth, colors) {
  const radius = 8;
  const padding = 0;
  const centerX = rectX + rectWidth + padding + radius;
  const centerY = rectY - padding - radius;

  ctx.fillStyle = colors.CLOSE_BTN;
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = colors.TEXT;
  ctx.lineWidth = 1.5;
  const crossSize = radius * 0.6;
  ctx.beginPath();
  ctx.moveTo(centerX - crossSize, centerY - crossSize);
  ctx.lineTo(centerX + crossSize, centerY + crossSize);
  ctx.moveTo(centerX + crossSize, centerY - crossSize);
  ctx.lineTo(centerX - crossSize, centerY + crossSize);
  ctx.stroke();

  return { centerX, centerY, radius };
}

// Draws one labelled component row (e.g. "X" + its value) — a rounded letter cell on the
// left and a value cell on the right, at vertical position rowY.
function drawComponentRow(ctx, geom, label, value, axisColor, rowY, colors) {
  ctx.fillStyle = axisColor;
  roundRect(ctx, geom.letterRecLeft, rowY, geom.letterRecWidth, 30, 15, 'left', true);

  ctx.fillStyle = colors.TEXT;
  ctx.fillText(label, geom.letterLeft, rowY + 20);

  roundRect(ctx, geom.textRecLeft, rowY, geom.textRecWidth, 30, 15, 'right', true);

  ctx.fillStyle = axisColor;
  const textLeft = geom.letterRecLeft + geom.letterRecWidth + (geom.textRecWidth - ctx.measureText(value).width) / 2;
  ctx.fillText(value, textLeft, rowY + 20);
}

// Draws the distance box, close button, and X/Y/Z component panels. Returns closeIconInfo.
function drawDistanceBoxAndPanels(ctx, { p1, p2, distanceText, differences, isMeasuring2D, colors }) {
  ctx.strokeStyle = colors.TRANSPARENT;
  ctx.font = '14px Arial';
  const letterDimension = ctx.measureText('X');
  const middle = midpoint(p1, p2);

  const textOffset = ctx.measureText(distanceText);
  const totalWidth = textOffset.width + letterDimension.width * 2;

  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  const isNearVertical = Math.abs(angle) > 45 && Math.abs(angle) < 125;

  const factor = isMeasuring2D ? 1.5 : 3;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const isSmall = distance < totalWidth * factor + 100;

  // For short measurements, push the text box off the line and draw a dashed connector.
  let textPosition = middle;
  if (isSmall) {
    ctx.beginPath();
    ctx.strokeStyle = colors.MAIN_LINE;
    ctx.setLineDash([2, 2]);
    ctx.moveTo(middle.x, middle.y);
    if (isNearVertical) {
      textPosition = { x: middle.x + totalWidth * 2, y: middle.y };
      ctx.lineTo(textPosition.x - 20, textPosition.y);
    } else {
      textPosition = { x: middle.x, y: middle.y + totalWidth * 1.5 };
      ctx.lineTo(textPosition.x, textPosition.y - 15);
    }
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Distance box background.
  ctx.fillStyle = colors.MAIN_LINE;
  const left = textPosition.x - totalWidth / 2;
  const rectX = left - 20;
  const rectY = textPosition.y - 15;
  const rectWidth = totalWidth + 40;
  roundRect(ctx, rectX, rectY, rectWidth, 30, 15, 'all', true);

  const closeIconInfo = drawCloseIcon(ctx, rectX, rectY, rectWidth, colors);

  // Distance text over the box.
  ctx.fillStyle = colors.TEXT;
  ctx.fillText(distanceText, left + letterDimension.width, textPosition.y + 5);

  // X / Y / Z component panels below the box.
  const geom = {
    letterRecLeft: left - 20,
    letterRecWidth: letterDimension.width * 2 + 20,
    letterLeft: left - 10 - letterDimension.width / 2 + letterDimension.width,
    textRecLeft: left + letterDimension.width * 2,
    textRecWidth: totalWidth + 20 - letterDimension.width * 2,
  };
  ctx.strokeStyle = colors.TRANSPARENT;
  const componentsYOffset = 20;
  const baseY = textPosition.y + componentsYOffset;
  drawComponentRow(ctx, geom, 'X', `${differences.x}`, colors.AXIS_X, baseY, colors);
  drawComponentRow(ctx, geom, 'Y', `${differences.y}`, colors.AXIS_Y, baseY + 35, colors);
  if (!isMeasuring2D) {
    drawComponentRow(ctx, geom, 'Z', `${differences.z}`, colors.AXIS_Z, baseY + 70, colors);
  }

  return closeIconInfo;
}

/**
 * Render a full measurement overlay onto `ctx`. The caller must clear the canvas and supply
 * pre-projected screen points; this function does not touch the viewer, store, or DOM.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {object} opts
 * @param {{x:number,y:number}} opts.p1 - start point in canvas pixels
 * @param {{x:number,y:number}} opts.p2 - end point in canvas pixels
 * @param {string} opts.distanceText - distance label, already prefixed (e.g. "~ 1.39 m")
 * @param {{x:string,y:string,z?:string}} opts.differences - per-axis component labels
 * @param {boolean} opts.isMeasuring2D - when true, omit the Z panel
 * @param {object} [opts.colors=MEASUREMENT_COLORS]
 * @returns {{centerX:number,centerY:number,radius:number}} close-icon hit info
 */
export function drawMeasurement(ctx, { p1, p2, distanceText, differences, isMeasuring2D, colors = MEASUREMENT_COLORS }) {
  // Main line.
  ctx.lineWidth = 3;
  ctx.strokeStyle = colors.MAIN_LINE;
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(p1.x, p1.y);
  ctx.lineTo(p2.x, p2.y);
  ctx.stroke();
  ctx.lineWidth = 1;

  // Endpoint circles.
  drawIntersectionCircle(ctx, p1, colors);
  drawIntersectionCircle(ctx, p2, colors);

  // Distance box, close button, and component panels.
  return drawDistanceBoxAndPanels(ctx, { p1, p2, distanceText, differences, isMeasuring2D, colors });
}
